import React, { useState } from 'react';
import { usePaymentsContext } from '../../shared/providers/PaymentsProvider';
import { ERROR_MESSAGES } from '../../shared/constants';
import { validateStripeId } from '../../shared/utils/validation';

interface BillingPortalProps {
  customerId: string;
  returnUrl?: string;
  children?: React.ReactNode;
  className?: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onLoading?: (loading: boolean) => void;
}

export const BillingPortal: React.FC<BillingPortalProps> = ({
  customerId,
  returnUrl = typeof window !== 'undefined' ? window.location.href : '/',
  children,
  className = '',
  onSuccess,
  onError,
  onLoading,
}) => {
  const { initialized } = usePaymentsContext();
  const [loading, setLoading] = useState(false);

  const handleOpenPortal = async () => {
    if (!initialized) {
      const error = ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED;
      onError?.(error);
      console.error(error);
      return;
    }

    if (!validateStripeId(customerId, 'customer')) {
      const error = ERROR_MESSAGES.INVALID_CUSTOMER_ID;
      onError?.(error);
      console.error(error);
      return;
    }

    setLoading(true);
    onLoading?.(true);

    try {
      const response = await fetch('/api/payments/create-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId,
          returnUrl,
        }),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: 'Network error' }));
        throw new Error(errorData.error || 'Failed to create portal session');
      }

      const { url } = await response.json();

      if (!url) {
        throw new Error('No portal URL returned from server');
      }

      // Redirect to Stripe Customer Portal
      window.location.href = url;
      onSuccess?.();
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to open billing portal';
      onError?.(errorMessage);
      console.error('Billing portal error:', error);
    } finally {
      setLoading(false);
      onLoading?.(false);
    }
  };

  const isDisabled = loading || !initialized || !customerId;

  return (
    <button
      onClick={handleOpenPortal}
      disabled={isDisabled}
      className={`
        inline-flex items-center justify-center px-4 py-2 
        border border-gray-300 text-sm font-medium rounded-md 
        text-gray-700 bg-white hover:bg-gray-50 
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
        disabled:opacity-50 disabled:cursor-not-allowed
        transition-colors duration-200
        ${className}
      `}
      aria-label="Manage billing"
    >
      {loading ? (
        <>
          <svg
            className="animate-spin -ml-1 mr-3 h-4 w-4 text-gray-700"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          Loading...
        </>
      ) : (
        children || (
          <>
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            Manage Billing
          </>
        )
      )}
    </button>
  );
};

export default BillingPortal;
